// /frontend/src/pages/admin/AdminOrders.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import { FaEye, FaSearch, FaFilter, FaSortAmountDown, FaTruck, FaRegClock, FaCheck, FaMoneyCheck } from 'react-icons/fa';
import { orderService } from '../../services/orderService';
import { toast } from 'react-toastify';

// Helper function to convert base64 to blob for PDFs
const b64toBlob = (b64Data, contentType = '', sliceSize = 512) => {
  const byteCharacters = atob(b64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);
    const byteNumbers = new Array(slice.length);
    
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays, { type: contentType });
};

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filtering and search state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPaymentStatus, setFilterPaymentStatus] = useState('');
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const ordersPerPage = 10;
  
  // Payment verification modal state
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [verificationNote, setVerificationNote] = useState('');
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  
  // Status dropdown state - map to track dropdown visibility for each order
  const [statusDropdowns, setStatusDropdowns] = useState({});
  
  // Status options
  const statuses = [
    'pending',
    'processing',
    'shipped',
    'delivered',
    'cancelled'
  ];
  
  // Payment status options
  const paymentStatuses = [
    'pending',
    'paid',
    'rejected',
    'refunded'
  ];
  
  // Fetch orders
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        // Replace with your actual API call when implemented
        const data = await orderService.getOrders();
        
        const ordersArray = data.orders || [];
        setOrders(ordersArray);
        setLoading(false);
      } catch (err) {
        setError(err.message || 'Failed to fetch orders');
        setLoading(false);
      }
    };
    
    fetchOrders();
  }, []);
  
  // Toggle dropdown visibility for a specific order
  const toggleStatusDropdown = (orderId) => {
    setStatusDropdowns(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };
  
  // Handle update order status
  const handleUpdateStatus = async (id, newStatus) => {
    try {
      // Replace with your actual API call when implemented
      await orderService.updateOrderStatus(id, { status: newStatus });
      
      // Update order status in the state
      setOrders(orders.map(order => 
        order._id === id ? { ...order, status: newStatus } : order
      ));
      
      // Hide the dropdown after selection
      setStatusDropdowns(prev => ({
        ...prev,
        [id]: false
      }));
      
      toast.success('Order status updated successfully');
    } catch (err) {
      toast.error(`Failed to update order status: ${err.message}`);
    }
  };
  
  // Open payment verification modal
  const openVerificationModal = (order) => {
    setSelectedOrder(order);
    setVerificationNote('');
    setShowVerificationModal(true);
  };
  
  // Handle verifying payment
  const handleVerifyPayment = async (status) => {
    if (!selectedOrder) return;
    
    try {
      setVerifyingPayment(true);
      
      await orderService.verifyPayment(selectedOrder._id, {
        status,
        notes: verificationNote
      });
      
      // Update order in state
      setOrders(orders.map(order => 
        order._id === selectedOrder._id 
          ? { 
              ...order, 
              paymentStatus: status, 
              isPaid: status === 'paid',
              paidAt: status === 'paid' ? new Date().toISOString() : order.paidAt,
              status: status === 'paid' ? 'processing' : order.status
            }
          : order
      ));
      
      setShowVerificationModal(false);
      toast.success(`Payment ${status === 'paid' ? 'approved' : 'rejected'} successfully`);
    } catch (err) {
      toast.error(`Failed to ${status === 'paid' ? 'approve' : 'reject'} payment: ${err.message}`);
    } finally {
      setVerifyingPayment(false);
    }
  };
  
  // View PDF from base64 data
  const viewPdf = (data, contentType) => {
    const blob = b64toBlob(data, contentType);
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');
  };
  
  // Filter and sort orders
  const filteredOrders = orders
    .filter(order => {
      // Search by order ID or customer name
      return order._id.toLowerCase().includes(searchTerm.toLowerCase()) || 
             (order.user && order.user.name && order.user.name.toLowerCase().includes(searchTerm.toLowerCase()));
    })
    .filter(order => filterStatus ? order.status === filterStatus : true)
    .filter(order => filterPaymentStatus ? order.paymentStatus === filterPaymentStatus : true)
    .sort((a, b) => {
      if (sortField === 'date') {
        return sortDirection === 'asc' 
          ? new Date(a.createdAt) - new Date(b.createdAt)
          : new Date(b.createdAt) - new Date(a.createdAt);
      } else if (sortField === 'total') {
        return sortDirection === 'asc' ? a.totalPrice - b.totalPrice : b.totalPrice - a.totalPrice;
      } else if (sortField === 'status') {
        return sortDirection === 'asc'
          ? (a.status || '').localeCompare(b.status || '')
          : (b.status || '').localeCompare(a.status || '');
      } else if (sortField === 'payment') {
        return sortDirection === 'asc'
          ? (a.paymentStatus || 'pending').localeCompare(b.paymentStatus || 'pending')
          : (b.paymentStatus || 'pending').localeCompare(a.paymentStatus || 'pending');
      }
      return 0;
    });
  
  // Pagination logic
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);
  
  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  
  // Get status badge style
  const getStatusBadgeStyle = (status) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'shipped':
        return 'bg-indigo-100 text-indigo-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  // Get payment status badge style
  const getPaymentStatusBadgeStyle = (status) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'refunded':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  // Format status text
  const formatStatusText = (status) => {
    if (!status) return 'Pending';
    return status.charAt(0).toUpperCase() + status.slice(1);
  };
  
  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-800"></div>
        </div>
      </AdminLayout>
    );
  }
  
  if (error) {
    return (
      <AdminLayout>
        <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-md mb-6">
          <p>{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="mt-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      </AdminLayout>
    );
  }
  
  return (
    <AdminLayout>
      {/* Actions */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">Order Management</h1>
      </div>
      
      {/* Filters and Search */}
      <div className="bg-white shadow-md rounded-lg p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <input
                type="text"
                placeholder="Search by order ID or customer name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
              <FaSearch className="absolute left-3 top-3 text-gray-400" />
            </div>
          </div>
          
          {/* Status Filter */}
          <div className="w-48">
            <div className="relative">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md appearance-none focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="">All Statuses</option>
                {statuses.map((status) => (
                  <option key={status} value={status}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </option>
                ))}
              </select>
              <FaFilter className="absolute left-3 top-3 text-gray-400" />
            </div>
          </div>
          
          {/* Payment Status Filter */}
          <div className="w-48">
            <div className="relative">
              <select
                value={filterPaymentStatus}
                onChange={(e) => setFilterPaymentStatus(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md appearance-none focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="">All Payment Statuses</option>
                {paymentStatuses.map((status) => (
                  <option key={status} value={status}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </option>
                ))}
              </select>
              <FaMoneyCheck className="absolute left-3 top-3 text-gray-400" />
            </div>
          </div>
          
          {/* Sorting */}
          <div className="w-48">
            <div className="relative">
              <select
                value={`${sortField}-${sortDirection}`}
                onChange={(e) => {
                  const [field, direction] = e.target.value.split('-');
                  setSortField(field);
                  setSortDirection(direction);
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md appearance-none focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="date-desc">Date (Newest First)</option>
                <option value="date-asc">Date (Oldest First)</option>
                <option value="total-desc">Total (High to Low)</option>
                <option value="total-asc">Total (Low to High)</option>
                <option value="status-asc">Status (A-Z)</option>
                <option value="status-desc">Status (Z-A)</option>
                <option value="payment-asc">Payment Status (A-Z)</option>
                <option value="payment-desc">Payment Status (Z-A)</option>
              </select>
              <FaSortAmountDown className="absolute left-3 top-3 text-gray-400" />
            </div>
          </div>
        </div>
      </div>
      
      {/* Orders Table */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        {currentOrders.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Order
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payment
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentOrders.map((order) => (
                  <tr key={order._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{order._id}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {order.user && order.user.name ? order.user.name : 'Unknown Customer'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {order.user && order.user.email ? order.user.email : ''}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {new Date(order.createdAt).toLocaleDateString()}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(order.createdAt).toLocaleTimeString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">${order.totalPrice?.toFixed(2)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeStyle(order.status)}`}>
                        {formatStatusText(order.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col space-y-1">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getPaymentStatusBadgeStyle(order.paymentStatus || 'pending')}`}>
                          {formatStatusText(order.paymentStatus || 'pending')}
                        </span>
                        <span className="text-xs text-gray-500">
                          {order.paymentMethod ? order.paymentMethod.replace('_', ' ') : 'Not selected'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end items-center space-x-3">
                        <div className="relative">
                          <button 
                            className="text-gray-500 hover:text-gray-700"
                            onClick={() => toggleStatusDropdown(order._id)}
                            title="Update Status"
                          >
                            <FaTruck />
                          </button>
                          
                          {/* Status update dropdown */}
                          {statusDropdowns[order._id] && (
                            <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                              <div className="py-1">
                                {statuses.map((status) => (
                                  <button
                                    key={status}
                                    onClick={() => handleUpdateStatus(order._id, status)}
                                    className={`${
                                      order.status === status ? 'bg-gray-100' : ''
                                    } block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100`}
                                    disabled={order.status === status}
                                  >
                                    {status.charAt(0).toUpperCase() + status.slice(1)}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Payment verification button */}
                        {order.paymentEvidence && (
                          <button
                            onClick={() => openVerificationModal(order)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Verify Payment"
                          >
                            <FaMoneyCheck />
                          </button>
                        )}
                        
                        <Link 
                          to={`/admin/orders/${order._id}`}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="View Details"
                        >
                          <FaEye />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-8 text-center">
            <p className="text-gray-500 mb-4">No orders found matching your criteria.</p>
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterStatus('');
                setFilterPaymentStatus('');
              }}
              className="text-teal-600 hover:text-teal-800 font-medium text-sm"
            >
              Clear filters
            </button>
          </div>
        )}
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 bg-gray-50 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{indexOfFirstOrder + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(indexOfLastOrder, filteredOrders.length)}
                  </span>{' '}
                  of <span className="font-medium">{filteredOrders.length}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-2 py-2 rounded-l-md border text-sm font-medium ${
                      currentPage === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    <span className="sr-only">Previous</span>
                    &larr;
                  </button>
                  
                  {/* Page numbers */}
                  {[...Array(totalPages)].map((_, index) => {
                    const pageNumber = index + 1;
                    const isCurrentPage = pageNumber === currentPage;
                    
                    // Only show a limited number of page buttons
                    if (
                      pageNumber === 1 ||
                      pageNumber === totalPages ||
                      (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={pageNumber}
                          onClick={() => setCurrentPage(pageNumber)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            isCurrentPage
                              ? 'z-10 bg-teal-50 border-teal-500 text-teal-600'
                              : 'bg-white text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {pageNumber}
                        </button>
                      );
                    }
                    
                    // Add ellipsis
                    if (
                      (pageNumber === currentPage - 2 && pageNumber > 1) ||
                      (pageNumber === currentPage + 2 && pageNumber < totalPages)
                    ) {
                      return (
                        <span
                          key={pageNumber}
                          className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-gray-700"
                        >
                          ...
                        </span>
                      );
                    }
                    
                    return null;
                  })}
                  
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className={`relative inline-flex items-center px-2 py-2 rounded-r-md border text-sm font-medium ${
                      currentPage === totalPages
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    <span className="sr-only">Next</span>
                    &rarr;
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Payment Verification Modal */}
      {showVerificationModal && selectedOrder && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                      Verify Payment
                    </h3>
                    
                    {/* Order details */}
                    <div className="mb-4 bg-gray-50 p-3 rounded-md">
                      <div className="flex justify-between mb-1">
                        <span className="text-sm text-gray-600">Order ID:</span>
                        <span className="text-sm font-medium">{selectedOrder._id}</span>
                      </div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm text-gray-600">Amount:</span>
                        <span className="text-sm font-medium">${selectedOrder.totalPrice?.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Payment Method:</span>
                        <span className="text-sm font-medium">
                          {selectedOrder.paymentMethod
                            ? selectedOrder.paymentMethod.charAt(0).toUpperCase() + selectedOrder.paymentMethod.slice(1).replace('_', ' ')
                            : 'Not specified'}
                        </span>
                      </div>
                    </div>
                    
                    {/* Payment Evidence */}
                    {selectedOrder.paymentEvidence && (
                      <div className="mb-4">
                        <h4 className="text-sm font-medium mb-2">Payment Evidence:</h4>
                        
                        {selectedOrder.paymentEvidence.contentType && selectedOrder.paymentEvidence.contentType.startsWith('image/') ? (
                          <img 
                            src={`data:${selectedOrder.paymentEvidence.contentType};base64,${selectedOrder.paymentEvidence.data}`}
                            alt="Payment Evidence"
                            className="max-w-full h-auto rounded border mx-auto"
                            style={{ maxHeight: '200px' }}
                          />
                        ) : selectedOrder.paymentEvidence.contentType === 'application/pdf' ? (
                          <div className="text-center">
                            <button
                              onClick={() => viewPdf(selectedOrder.paymentEvidence.data, selectedOrder.paymentEvidence.contentType)}
                              className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                              View PDF
                            </button>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-600 text-center">File uploaded but cannot be displayed</p>
                        )}
                        
                        {selectedOrder.paymentEvidenceUploadedAt && (
                          <p className="text-xs text-gray-500 text-center mt-2">
                            Uploaded: {new Date(selectedOrder.paymentEvidenceUploadedAt).toLocaleString()}
                          </p>
                        )}
                      </div>
                    )}
                    
                    {/* Verification Notes */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Verification Notes (optional)
                      </label>
                      <textarea
                        value={verificationNote}
                        onChange={(e) => setVerificationNote(e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                        rows="3"
                        placeholder="Add notes about this payment verification..."
                      ></textarea>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => handleVerifyPayment('paid')}
                  disabled={verifyingPayment}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {verifyingPayment ? 'Processing...' : 'Approve Payment'}
                </button>
                <button
                  type="button"
                  onClick={() => handleVerifyPayment('rejected')}
                  disabled={verifyingPayment}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {verifyingPayment ? 'Processing...' : 'Reject Payment'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowVerificationModal(false)}
                  disabled={verifyingPayment}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminOrders;